
import { useState } from 'react';
import { type UsageStats } from '../types'

interface Props {
  usage: UsageStats
  onRefresh?: () => void;
  selectedMonth?: string;
}

export default function UsageStatsSection({ usage, onRefresh, selectedMonth }: Props) {
  const [refreshing, setRefreshing] = useState(false);
  const totalUsage = usage.current_month || 0;
  const creditsUsed = usage.credits_used || 0;
  
  // Calculate percentage based on credits used vs total available
  // monthly_limit = current balance + credits used this month
  // So percentage = credits used / (current balance + credits used)
  const percentage = usage.monthly_limit > 0 ? (creditsUsed / usage.monthly_limit) * 100 : 0;
  const isNearLimit = percentage >= 80
  const isAtLimit = usage.remaining <= 0 // No credits remaining
  
  // Get month name from selectedMonth or current month
  let currentMonth: string;
  if (selectedMonth) {
    const date = new Date(selectedMonth + '-01');
    currentMonth = date.toLocaleString('default', { month: 'long', year: 'numeric' });
  } else {
    currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
  }

  const handleRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true);
      await onRefresh();
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  return (
    <div className="glass-card rounded-xl p-6 hover-lift">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <svg className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Usage Statistics
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{currentMonth}</p>
        </div>
        <div className="flex items-center space-x-3">
          {onRefresh && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg bg-white/50 dark:bg-slate-800/50 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-slate-700 transition disabled:opacity-50"
              title="Refresh usage data"
            >
              <svg 
                className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Usage This Month</span>
            <span className={`text-sm font-bold ${
              isAtLimit ? 'text-red-600 dark:text-red-400' 
              : isNearLimit ? 'text-yellow-600 dark:text-yellow-400' 
              : 'text-gray-900 dark:text-white'
            }`}>
              {creditsUsed} credits used
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                isAtLimit 
                  ? 'bg-red-500 dark:bg-red-600' 
                  : isNearLimit 
                  ? 'bg-yellow-500 dark:bg-yellow-600' 
                  : 'bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-400 dark:to-primary-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          {isNearLimit && !isAtLimit && (
            <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-400 flex items-center animate-fade-in">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              You're approaching your monthly limit
            </p>
          )}
          {isAtLimit && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center animate-fade-in">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Monthly limit reached. Upgrade to continue.
            </p>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-200/50 dark:border-slate-700/50">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {usage.current_month}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Requests Used</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-1">
              {usage.credits_used || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Credits Used</div>
          </div>
          <div className="text-center">
            <div className={`text-3xl font-bold mb-1 ${
              usage.remaining > 0 
                ? 'text-primary-600 dark:text-primary-400' 
                : 'text-red-600 dark:text-red-400'
            }`}>
              {usage.remaining}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Credits Remaining</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {usage.monthly_limit}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Available</div>
          </div>
        </div>

        {/* Source Breakdown */}
        {usage.by_source && Object.values(usage.by_source).some((val) => {
          const v = val as number | { count: number; credits: number };
          return typeof v === 'number' ? v > 0 : (v.count > 0 || v.credits > 0);
        }) && (
          <div className="pt-6 border-t border-gray-200/50 dark:border-slate-700/50">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Usage by Source</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(usage.by_source)
                .filter(([_, val]) => {
                  const v = val as number | { count: number; credits: number };
                  return typeof v === 'number' ? v > 0 : (v.count > 0 || v.credits > 0);
                })
                .map(([source, val]) => {
                    // Parse source to extract app/extension names
                    // Sources might be: "extension", "extension-chrome", "extension-firefox", "app-shopify", etc.
                    let sourceKey = source;
                    let appName = '';
                    
                    if (source.startsWith('extension-')) {
                      sourceKey = 'extension';
                      appName = source.replace('extension-', '').split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ');
                    } else if (source.startsWith('app-')) {
                      sourceKey = 'app';
                      appName = source.replace('app-', '').split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ');
                    }
                    
                    const sourceLabels: Record<string, { label: string; icon: string; logo?: string; logos?: string[]; color: string }> = {
                      web: { label: 'Web Dashboard', icon: '🌐', color: 'blue' },
                      api: { label: 'API Call', icon: '🔌', color: 'green' },
                      extension: { label: appName ? `${appName} Extension` : 'Browser Extension', icon: '🔗', color: 'purple' },
                      app: { label: appName ? `${appName} App` : 'App', icon: '📱', color: 'green' },
                      'wordpress-atg': { label: 'WordPress', icon: '📝', logo: '/wordpress-logo3.webp', color: 'blue' },
                      'wordpress-tts': { label: 'WordPress', icon: '🎙️', logo: '/wordpress-logo3.webp', color: 'indigo' },
                      wordpress: { label: 'WordPress', icon: '📝', logo: '/wordpress-logo3.webp', color: 'blue' },
                      shopify: { label: 'Shopify App', icon: '🛍️', logo: '/shopify.png', color: 'green' },
                      batch: { label: 'Batch Processing', icon: '📦', color: 'orange' },
                      tts: { label: 'Text-to-Speech', icon: '🎙️', color: 'indigo' },
                    };
                    
                    // Handle both old format (number) and new format ({ count, credits })
                    const sourceData = typeof val === 'number' 
                      ? { count: val, credits: 0 }
                      : val as { count: number; credits: number };
                    const numericCount = sourceData.count;
                    const numericCredits = sourceData.credits;
                    const sourceInfo = sourceLabels[sourceKey] || sourceLabels[source] || { label: source, icon: '📊', color: 'gray' };
                    
                    // Fix percentage calculation - avoid division by zero
                    const sourcePercentage = totalUsage > 0 ? (numericCount / totalUsage) * 100 : 0;
                  
                    return (
                      <div
                        key={source}
                      className={`p-3 rounded-lg border ${
                        sourceInfo.color === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' :
                        sourceInfo.color === 'green' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                        sourceInfo.color === 'purple' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' :
                        sourceInfo.color === 'orange' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' :
                        sourceInfo.color === 'indigo' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800' :
                        'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {sourceInfo.logos && sourceInfo.logos.length > 0 ? (
                            // Show multiple icons/logos (e.g., mic icon + WordPress logo)
                            <div className="flex items-center space-x-1">
                              <span className="text-lg">{sourceInfo.icon}</span>
                              {sourceInfo.logos.map((logo, idx) => (
                                <img
                                  key={idx}
                                  src={logo}
                                  alt={sourceInfo.label}
                                  className="w-5 h-5 object-contain"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement
                                    target.style.display = 'none'
                                  }}
                                />
                              ))}
                            </div>
                          ) : sourceInfo.logo ? (
                            <img
                              src={sourceInfo.logo}
                              alt={sourceInfo.label}
                              className="w-5 h-5 object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                                const parent = target.parentElement
                                if (parent) {
                                  const fallback = document.createElement('span')
                                  fallback.className = 'text-lg'
                                  fallback.textContent = sourceInfo.icon
                                  parent.appendChild(fallback)
                                }
                              }}
                            />
                          ) : (
                            <span className="text-lg">{sourceInfo.icon}</span>
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {sourceInfo.label}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-gray-900 dark:text-white">
                            {numericCount} requests
                          </div>
                          {numericCredits > 0 && (
                            <div className="text-xs text-primary-600 dark:text-primary-400">
                              {numericCredits} credits
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            sourceInfo.color === 'blue' ? 'bg-blue-500' :
                            sourceInfo.color === 'green' ? 'bg-green-500' :
                            sourceInfo.color === 'purple' ? 'bg-purple-500' :
                            sourceInfo.color === 'orange' ? 'bg-orange-500' :
                            sourceInfo.color === 'indigo' ? 'bg-indigo-500' :
                            'bg-gray-500'
                          }`}
                          style={{ width: `${Math.min(sourcePercentage, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {sourcePercentage > 0 ? `${sourcePercentage.toFixed(1)}%` : '0%'} of total usage
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Tool Breakdown (Alt Text vs TTS) */}
        {usage.by_tool && Object.values(usage.by_tool).some((count) => (count as number) > 0) && (
          <div className="pt-6 border-t border-gray-200/50 dark:border-slate-700/50">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Usage by Tool</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(usage.by_tool)
                .filter(([_, count]) => (count as number) > 0)
                .map(([toolSlug, count]) => {
                  const toolLabels: Record<string, { label: string; icon: string; color: string }> = {
                    'alt-text': { label: 'Alt Text API', icon: '🖼️', color: 'blue' },
                    tts: { label: 'TTS API', icon: '🎙️', color: 'indigo' },
                    'blog-generator': { label: 'Gist Blog', icon: '📝', color: 'purple' },
                  };

                  const toolInfo = toolLabels[toolSlug] || { label: toolSlug, icon: '🛠️', color: 'gray' };
                  const numericCount = count as number;
                  const toolPercentage = totalUsage > 0 ? (numericCount / totalUsage) * 100 : 0;

                  return (
                    <div
                      key={toolSlug}
                      className={`p-3 rounded-lg border ${
                        toolInfo.color === 'blue'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                          : toolInfo.color === 'indigo'
                          ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800'
                          : toolInfo.color === 'purple'
                          ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{toolInfo.icon}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {toolInfo.label}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-900 dark:text-white">
                          {numericCount}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            toolInfo.color === 'blue'
                              ? 'bg-blue-500'
                              : toolInfo.color === 'indigo'
                              ? 'bg-indigo-500'
                              : toolInfo.color === 'purple'
                              ? 'bg-purple-500'
                              : 'bg-gray-500'
                          }`}
                          style={{ width: `${Math.min(toolPercentage, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {toolPercentage > 0 ? `${toolPercentage.toFixed(1)}%` : '0%'} of total usage
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
